import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import { SubscriptionFormProps } from '.';
import { useSubscriptionUpdateForCbpt } from '../../hooks';
import { buildQueryString, extractQueryParams } from '../../utils/queryStringHelper';
import { CustomSpinner } from '../BaseUI';
import Disclosure from '../shared/Stripe/Disclosure';
import styles from './styles.module.scss';

interface PaymentMethodProps
  extends Omit<SubscriptionFormProps, 'userEmail' | 'userFullName' | 'dob'> {
  isTrial?: boolean;
}

const PaymentMethod = ({
  buttonText,
  currentTrialEndDate,
  isTrial,
  isUserCBPT,
  referringSubscriberName,
  referringSubscriberGuid,
  selectedOffer: { currentPrice, frequency, originalPrice, code },
  userNextBillingDate,
}: PaymentMethodProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: updateSubscriptionForCbpt } = useSubscriptionUpdateForCbpt();
  const protocol = window.location.protocol;
  const host = window.location.host;
  const { article_id, modal } = extractQueryParams(['article_id', 'modal']);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isUserCBPT) {
      setIsLoading(true);
      updateSubscriptionForCbpt({
        page: window.location.pathname,
        code,
        source: 'web',
      });
      setIsLoading(true);
      return;
    }

    if (!stripe || !elements) {
      setErrorMessage('Still loading Stripe. Please wait a few seconds and then try again.');
      return;
    }

    setIsLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsLoading(false);
      return;
    }

    const queryString = buildQueryString({
      article_id,
      modal,
      code,
      guid: referringSubscriberGuid,
    });
    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // Can't use READER_URL for testing, so this is used to build URLs dynamically
        return_url: `${protocol}//${host}/payment-status?${queryString}`,
      },
      redirect: 'always',
    });

    setIsLoading(false);
    if (error) {
      setErrorMessage(error.message);
      return;
    }
  };

  return (
    <>
      <div className="tw-mt-24 tw-mb-20 tw-flex tw-items-center">
        <div className="tw-text-primary-red tw-text-body-sm tw-uppercase tw-font-bold tw-font-sans">
          Payment Information
        </div>
        <div className="tw-ml-12 tw-flex-1 tw-bg-primary-red tw-h-2"></div>
      </div>
      {!isUserCBPT && (
        <div id="payment-form">
          <PaymentElement
            onChange={() => setErrorMessage('')}
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
            }}
          />
          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
        </div>
      )}
      <div className={styles.totalContainer}>
        <div className="tw-flex tw-items-start tw-justify-between">
          <div className={styles.todaysTotal}>
            {isUserCBPT && userNextBillingDate
              ? `You'll be charged on ${userNextBillingDate}`
              : "Today's Total"}
          </div>
          <div className="tw-text-right tw-flex tw-flex-col">
            <span className={styles.amount}>${isTrial ? '0.00' : currentPrice.toFixed(2)}</span>
            {isTrial && <span>${currentPrice.toFixed(2)} / Year after free trial</span>}
          </div>
        </div>
        {originalPrice && frequency && (
          <div className="tw-flex tw-text-gray-500">
            {frequency} after the offer concludes{' '}
            <span className="tw-ml-auto">${originalPrice.toFixed(2)}</span>
          </div>
        )}
      </div>
      {isTrial && (
        <div className={styles.chargeNote}>
          By beginning your trial, we will be sharing your name with {referringSubscriberName}, who
          has referred you, and letting them know that you&apos;ve started enjoying The Information.
        </div>
      )}
      <Disclosure />
      <Button
        onClick={handleSubmit}
        disabled={!stripe || isLoading}
        type="submit"
        className={clsx(styles.submitBtn, 'tw-mt-16')}
      >
        {isLoading ? (
          <CustomSpinner
            color="#fff"
            size={10}
            style={{ display: 'flex', justifyContent: 'center' }}
          />
        ) : (
          <>{buttonText}</>
        )}
      </Button>
      {isTrial && (
        <div className={styles.chargeNote}>
          if you cancel before the end of day on {currentTrialEndDate}, you will not be charged.
        </div>
      )}
    </>
  );
};

export default PaymentMethod;
